import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { memberAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  Banknote, 
  CreditCard, 
  User, 
  Calendar, 
  Users,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const MemberFinance = () => {
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCash, setExpandedCash] = useState({});
  const [expandedOnline, setExpandedOnline] = useState({});

  useEffect(() => {
    loadFinance();
  }, []);

  const loadFinance = async () => {
    try {
      const res = await memberAPI.getFinance();
      setFinance(res.data);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCashExpand = (guideId) => {
    setExpandedCash(prev => ({ ...prev, [guideId]: !prev[guideId] }));
  };

  const toggleOnlineExpand = (guideId) => {
    setExpandedOnline(prev => ({ ...prev, [guideId]: !prev[guideId] }));
  };

  if (loading) {
    return (
      <UserLayout title="Finance">
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout title="Finance & Reports">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-slate-300 text-sm">Total Spent on Guides</p>
                <p className="text-3xl font-bold">€{finance?.total_spent?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Banknote className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-amber-100 text-sm">Cash - Settle with Guides</p>
                <p className="text-3xl font-bold">€{finance?.cash_total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CreditCard className="w-10 h-10 opacity-80" />
              <div>
                <p className="text-blue-100 text-sm">Online - Platform Settled</p>
                <p className="text-3xl font-bold">€{finance?.online_total?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Next Settlement Date */}
      {finance?.next_settlement_date && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Next Weekly Settlement</p>
                <p className="text-sm text-blue-600">{finance.next_settlement_date}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cash Payments Section */}
      <Card className="mb-6">
        <CardHeader className="bg-amber-50 border-b border-amber-200">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <Banknote className="w-5 h-5" />
            Cash Payments - Settle Directly with Guides
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Warning Banner */}
          <div className="bg-amber-50 border-b border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Direct Settlement Required</p>
                <p>Please contact the guides below to arrange payment for cash tours.</p>
              </div>
            </div>
          </div>
          
          {finance?.cash_payments?.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <Banknote className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No cash payments to settle</p>
            </div>
          ) : (
            <div className="divide-y">
              {finance?.cash_payments?.map((guide) => (
                <div key={guide.guide_id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleCashExpand(guide.guide_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{guide.guide_name}</h4>
                        <p className="text-sm text-slate-500">{guide.total_tours} tour(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg text-amber-600">€{guide.total_amount.toFixed(2)}</p>
                        <Badge className="bg-amber-100 text-amber-700">To Pay</Badge>
                      </div>
                      {expandedCash[guide.guide_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedCash[guide.guide_id] && (
                    <div className="mt-4 pl-13 space-y-3">
                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-4 mb-3">
                        {guide.guide_email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${guide.guide_email}`} className="text-indigo-600 hover:underline">
                              {guide.guide_email}
                            </a>
                          </div>
                        )}
                        {guide.guide_phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${guide.guide_phone}`} className="text-indigo-600 hover:underline">
                              {guide.guide_phone}
                            </a>
                          </div>
                        )}
                      </div>
                      
                      {/* Tours List */}
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {guide.tours.map((tour, idx) => (
                          <div key={tour.id || idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                            <div>
                              <p className="font-medium">{tour.description || tour.location}</p>
                              <p className="text-slate-500 flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                {formatDateTime(tour.date)}
                                <Users className="w-3 h-3 ml-2" />
                                {tour.pax} pax
                              </p>
                            </div>
                            <p className="font-semibold text-amber-600">€{tour.amount?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Online Payments Section */}
      <Card>
        <CardHeader className="bg-blue-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <CreditCard className="w-5 h-5" />
            Online Payments - Settled Through Platform
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Info Banner */}
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Automatic Weekly Settlement</p>
                <p>Online payments are automatically settled with guides every week (1st, 8th, 15th, 22nd, 29th). A 10% platform fee is deducted.</p>
              </div>
            </div>
          </div>
          
          {finance?.online_payments?.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No online payments recorded</p>
            </div>
          ) : (
            <div className="divide-y">
              {finance?.online_payments?.map((guide) => (
                <div key={guide.guide_id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleOnlineExpand(guide.guide_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{guide.guide_name}</h4>
                        <p className="text-sm text-slate-500">{guide.total_tours} tour(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg text-blue-600">€{guide.total_amount.toFixed(2)}</p>
                        <Badge className="bg-blue-100 text-blue-700">Platform Settled</Badge>
                      </div>
                      {expandedOnline[guide.guide_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedOnline[guide.guide_id] && (
                    <div className="mt-4 pl-13 space-y-3">
                      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                        {guide.tours.map((tour, idx) => (
                          <div key={tour.id || idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-200 last:border-0">
                            <div>
                              <p className="font-medium">{tour.description || tour.location}</p>
                              <p className="text-slate-500 flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                {formatDateTime(tour.date)}
                                <Users className="w-3 h-3 ml-2" />
                                {tour.pax} pax
                              </p>
                            </div>
                            <p className="font-semibold text-blue-600">€{tour.amount?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default MemberFinance;
